import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, Mail, Calendar, FolderOpen, Loader2, ArrowRight, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { getOAuthAccountSelection, selectOAuthAccount } from '@/api/auth';

interface User {
  id: string;
  email: string;
  full_name: string | null;
  created_at: string | null;
}

export function OAuthAccountSelectPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<{
    provider: string;
    email: string;
    users: User[];
  } | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  useEffect(() => {
    if (!sessionId) {
      setError('Invalid session. Please try signing in again.');
      setLoading(false);
      return;
    }

    loadAccountSelection();
  }, [sessionId]);

  const loadAccountSelection = async () => {
    try {
      const response = await getOAuthAccountSelection(sessionId!);

      if (response.success) {
        setData({
          provider: response.provider!,
          email: response.email!,
          users: response.users!
        });
        // Auto-select the first user
        if (response.users && response.users.length > 0) {
          setSelectedUserId(response.users[0].id);
        }
      } else {
        setError(response.error || 'Failed to load account selection');
      }
    } catch (error) {
      setError('Failed to load account selection. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedUserId || !sessionId) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await selectOAuthAccount(sessionId, selectedUserId);

      if (response.success && response.redirect) {
        // Redirect to OAuth callback to complete the flow
        window.location.href = response.redirect;
      } else {
        setError(response.error || 'Failed to select account');
      }
    } catch (error) {
      setError('Failed to select account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading account options...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <img
              src="/images/doploy-logo.png"
              alt="Doploy"
              className="h-8 mx-auto mb-4"
            />
          </div>

          {/* Error Card */}
          <Card className="shadow-lg">
            <CardHeader className="text-center pb-4">
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <CardTitle className="text-2xl">Account Selection Error</CardTitle>
            </CardHeader>

            <CardContent className="text-center space-y-6">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>

              <Button onClick={() => navigate('/auth/login')} className="w-full">
                Return to Login
              </Button>

              <p className="text-xs text-gray-500">
                Need help? Contact us at{' '}
                <a
                  href="mailto:support@doploy.io"
                  className="text-blue-600 hover:text-blue-800"
                >
                  support@doploy.io
                </a>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="/images/doploy-logo.png"
            alt="Doploy"
            className="h-8 mx-auto mb-4"
          />
        </div>

        {/* Main Card */}
        <Card className="shadow-lg">
          <CardHeader className="text-center pb-4">
            <Users className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <CardTitle className="text-2xl">Multiple Accounts Found</CardTitle>
            <p className="text-gray-600 mt-2">
              Your {data.provider.charAt(0).toUpperCase() + data.provider.slice(1)} account has verified emails that match multiple doploy accounts.
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Warning Alert */}
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Multiple Accounts Detected</strong><br />
                Please select which doploy account you want to link to your {data.provider.charAt(0).toUpperCase() + data.provider.slice(1)} account.
              </AlertDescription>
            </Alert>

            {/* Account Selection */}
            <div className="space-y-4">
              <Label className="text-base font-medium">Select an account:</Label>

              <RadioGroup value={selectedUserId} onValueChange={setSelectedUserId}>
                {data.users.map((user) => (
                  <Card
                    key={user.id}
                    className={`cursor-pointer transition-all duration-200 ${
                      selectedUserId === user.id
                        ? 'ring-2 ring-blue-500 shadow-md'
                        : 'hover:shadow-sm'
                    }`}
                    onClick={() => setSelectedUserId(user.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-4">
                        <RadioGroupItem value={user.id} id={`user-${user.id}`} />

                        <div className="h-12 w-12 bg-blue-500 text-white rounded-full flex items-center justify-center font-medium">
                          {user.email.charAt(0).toUpperCase()}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {user.email}
                            </p>
                          </div>

                          {user.full_name && (
                            <p className="text-sm text-gray-600 mt-1">
                              {user.full_name}
                            </p>
                          )}

                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-3 w-3" />
                              <span>Created {formatDate(user.created_at)}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <FolderOpen className="h-3 w-3" />
                              <span>Member of projects</span>
                            </div>
                          </div>
                        </div>

                        <ArrowRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </RadioGroup>
            </div>

            {/* Error Display */}
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Submit Button */}
            <div className="flex space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => navigate('/auth/login')}
                className="flex-1"
                disabled={submitting}
              >
                Cancel
              </Button>

              <Button
                onClick={handleSubmit}
                disabled={!selectedUserId || submitting}
                className="flex-1"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Linking Account...
                  </>
                ) : (
                  <>
                    Link Account
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>

            {/* Footer */}
            <div className="text-center pt-4">
              <p className="text-xs text-gray-500">
                Need help? Contact us at{' '}
                <a
                  href="mailto:support@doploy.io"
                  className="text-blue-600 hover:text-blue-800"
                >
                  support@doploy.io
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
